import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Plane } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Airline autocomplete. Stores value as the airline display name (e.g.
 * "IndiGo") so the existing quotation schema/PDF renderers keep working
 * unchanged. Search kicks in from the 1st character.
 */
export default function AirlineSelect({ label, value, onChange, testid, placeholder = "Type airline name or IATA" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(0);
  const debRef = useRef();

  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    const query = q.trim();
    if (query.length < 1) { setResults([]); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${API}/airlines/search`, {
          params: { q: query, limit: 12 },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setResults(Array.isArray(data) ? data : []);
        setHover(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 150);
    return () => clearTimeout(debRef.current);
  }, [q, open]);

  const pick = (a) => {
    onChange(a.name);
    setOpen(false);
    setQ("");
  };

  const submitFreeText = () => {
    if (q.trim()) { onChange(q.trim()); setOpen(false); setQ(""); }
  };

  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setQ(value || ""); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="w-full border border-border rounded px-3 py-2 text-sm text-left flex items-center gap-2 bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            <Plane size={14} className="text-slate-400 shrink-0" />
            <span className={value ? "text-slate-900 truncate" : "text-slate-400 truncate"}>
              {value || placeholder}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[320px] p-0 overflow-hidden" data-testid={`${testid}-popover`}>
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              data-testid={`${testid}-input`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(h + 1, results.length - 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
                else if (e.key === "Enter") {
                  e.preventDefault();
                  if (results[hover]) pick(results[hover]);
                  else submitFreeText();
                }
                else if (e.key === "Escape") { setOpen(false); }
              }}
              placeholder="Type at least 3 letters (e.g. Ind, EK, Air)"
              className="w-full border border-slate-200 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {loading && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
            {!loading && q.trim().length < 1 && (
              <div className="px-3 py-2 text-xs text-slate-400">Type to search 100+ airlines</div>
            )}
            {!loading && q.trim().length >= 1 && results.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-400">
                No match — press Enter to keep "{q}" as-is.
              </div>
            )}
            {results.map((a, i) => (
              <button
                key={`${a.iata}-${a.icao}`}
                type="button"
                data-testid={`${testid}-option-${a.iata}`}
                onMouseEnter={() => setHover(i)}
                onClick={() => pick(a)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-slate-50 last:border-b-0 ${
                  i === hover ? "bg-emerald-50" : "hover:bg-slate-50"
                }`}
              >
                <span className="inline-flex items-center justify-center w-11 h-6 rounded bg-slate-900 text-white text-[10px] font-semibold tracking-widest shrink-0">
                  {a.iata}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-slate-900 truncate">{a.name}</span>
                  <span className="block text-[10px] text-slate-400 tracking-widest">{a.icao}</span>
                </span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
